import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Typography from '@mui/material/Typography'
import React, { useCallback } from 'react'
import { Controller } from 'react-hook-form'

import HHFormTimeField from 'components/form-fields/v5/HHFormTimeField'
import { WEEK_DAYS } from 'settings/constants/day'
import T from 'T'

import type { Control, ControllerRenderProps } from 'react-hook-form'

const panelSx = { border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }

const headingSx = { display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }

const labelSx = { color: 'text.secondary', display: 'block', mb: 0.75 }

// Round, single-letter and all the same width, so the row reads as a week rather than as seven words.
const daysSx = {
  flexWrap: 'wrap',
  gap: 0.75,
  '& .MuiToggleButton-root': { border: 1, borderColor: 'divider', borderRadius: '50%', width: 32, height: 32, p: 0 },
}

interface Props {
  // The dialog's form is untyped JS, so its control is not parameterised here.
  readonly control: Control<Record<string, unknown>>
}

const LocationServiceTimes = ({ control }: Props) => {
  const renderDays = useCallback((renderProps: { field: ControllerRenderProps<Record<string, unknown>, 'serviceAppliesOnDays'> }) => {
    const { field } = renderProps
    const selected = (field.value as number[] | undefined) ?? []
    return (
      <ToggleButtonGroup
        aria-label={T.APPLIES_ON}
        size="small"
        sx={daysSx}
        value={selected}
        // eslint-disable-next-line react/jsx-no-bind -- closes over the Controller's field
        onChange={(event, next: number[]) => field.onChange(next)}
      >
        {WEEK_DAYS.map((day, index) => (
          <ToggleButton key={day} aria-label={day} value={index}>
            {day.charAt(0)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    )
  }, [])

  return (
    <Box sx={panelSx}>
      <Box sx={headingSx}>
        <AccessTimeOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        <Typography variant="h6">{T.SERVICE_TIMES}</Typography>
      </Box>

      <Stack direction="row" spacing={2}>
        <HHFormTimeField control={control} label={T.SERVICE_AFTER} name="serviceAfter" size="small" sx={{ flex: '1 1 0' }} />
        <HHFormTimeField control={control} label={T.SERVICE_BEFORE} name="serviceBefore" size="small" sx={{ flex: '1 1 0' }} />
      </Stack>

      <Typography sx={{ ...labelSx, mt: 2 }} variant="caption">
        {T.APPLIES_ON}
      </Typography>
      <Controller control={control} name="serviceAppliesOnDays" render={renderDays} />
    </Box>
  )
}

export default LocationServiceTimes
